/* ============================================================
   033 DIGITAL — CENTRAL CONTENT CONFIG
   Frequently changing content lives here, not in components.
   ============================================================ */

export const SITE = {
    name: "033 Digital",
    domain: "https://www.033digital.com",
    tagline: "Get Found. Get Booked. Get Growing.",
    brandLine: "Marketing that grows businesses.",
    positioning: "AI-powered growth systems for local businesses.",
    location: "Jadavpur, Kolkata, West Bengal, India",
    email: "founder@033digital.com",
    whatsappNumber: "917303197877",
    whatsappDisplay: "+91 73031 97877",
    // When a calendar link exists, the Strategy Call CTA uses it instead of WhatsApp.
    calendarUrl: "",
    // Public Google Business Profile reviews page ("Read all reviews on Google").
    googleReviewsUrl: "https://search.google.com/local/reviews?placeid=ChIJLY5xQRFxAjoRm24K34l4v-w",
    // Opens Google's "write a review" form for the Business Profile.
    googleWriteReviewUrl: "https://search.google.com/local/writereview?placeid=ChIJLY5xQRFxAjoRm24K34l4v-w",
    // Social profiles — shown as buttons in the footer and closing section.
    instagramUrl: "https://www.instagram.com/033digitalagency/",
    facebookUrl: "https://www.facebook.com/033digitalagency/",
    logoFull: "/assets/logo-full.webp",
    logoMark: "/assets/logo-mark.webp",
};

export const WA_MESSAGES = {
    scan: "I'd like to get my business scanned.",
    grow: "I'd like to discuss growth for my business.",
    call: "I'd like to book a strategy call.",
};

export const NAV_LINKS = [
    { label: "The Leaks", id: "leaks" },
    { label: "Growth System", id: "growth-system" },
    { label: "How It Works", id: "how-it-works" },
    { label: "Results", id: "results" },
    { label: "About", id: "about" },
    { label: "FAQ", id: "faq" },
];

export const LEAKS = [
    {
        icon: "Search",
        title: "Hard to find",
        text: "Someone nearby searches for exactly what you do — and your business doesn't show up.",
    },
    {
        icon: "Star",
        title: "Weak first impression",
        text: "Someone finds you, but the profile looks thin, unmanaged or out of date.",
    },
    {
        icon: "MousePointerClick",
        title: "No clear next step",
        text: "Someone visits your website, looks around, and leaves — because nothing tells them what to do next.",
    },
    {
        icon: "PhoneMissed",
        title: "Unanswered calls",
        text: "Someone calls, nobody picks up, and the moment — and the customer — is gone.",
    },
    {
        icon: "MessageSquare",
        title: "Follow-up never happens",
        text: "Someone enquires, hears nothing back, and books with whoever replied first.",
    },
    {
        icon: "BellOff",
        title: "No reminders",
        text: "Someone books, never gets reminded, and simply doesn't show up.",
    },
    {
        icon: "StarOff",
        title: "Silent customers",
        text: "Someone has a great experience, but is never asked to leave a review.",
    },
    {
        icon: "UserX",
        title: "One-and-done",
        text: "Someone used your service once — and is never contacted again.",
    },
];

export const GROWTH_STEPS = [
    {
        num: "01",
        name: "GET FOUND",
        title: "Be the first business nearby customers see.",
        text: "When someone near you searches, your Google Business Profile and website do the convincing before you ever pick up the phone.",
        items: [
            "Google Business Profile setup & optimisation",
            "Local SEO to show up on Google Maps & Search",
            "Regular photos, posts & offers on your profile",
            "Review replies that build trust",
            "Conversion-focused, mobile-first website",
            "Click-to-call & WhatsApp buttons on every page",
        ],
    },
    {
        num: "02",
        name: "GET BOOKED",
        title: "WhatsApp automation that fills your calendar.",
        text: "Every enquiry gets a reply, every booking gets a reminder, and every visit ends with a reason to come back.",
        badge: "WhatsApp automation",
        items: [
            "Appointment booking & confirmations",
            "Appointment follow-ups to reduce no-shows",
            "Google review request after every visit",
            "Bring old customers back",
            "Festive & promotional messages",
            "Bulk offer campaigns without getting your number blocked",
        ],
    },
    {
        num: "03",
        name: "GET GROWING",
        status: "Coming soon",
        title: "Reach more people, every month.",
        text: "Consistent content and targeted ads keep new customers flowing into the top of the funnel.",
        items: [
            "Content creation",
            "Social media management",
            "Meta ads",
            "Google ads",
            "Instagram ads",
        ],
    },
];

export const BEFORE = [
    "Weak online presence — hard to find, easy to scroll past",
    "Visitors arrive but don't know what to do next",
    "Enquiries and calls slip through unanswered",
    "Every follow-up depends on someone remembering",
    "Happy customers never become public reviews",
    "Past customers are never contacted again",
];

export const AFTER = [
    "Stronger discovery across Google and local search",
    "A clear conversion path on every page and profile",
    "Faster follow-up on every enquiry",
    "Automated customer journeys — reminders, rebooking, reconnection",
    "Consistent review collection, built into the flow",
    "Customer reactivation that brings people back",
];

export const HOW_IT_WORKS = [
    {
        num: "01",
        title: "Business Scan",
        text: "We look at how customers find you, contact you, book you, review you and come back — and show you exactly where they slip away.",
    },
    {
        num: "02",
        title: "Growth Plan",
        text: "You get a clear, prioritised plan: what to fix first, why it matters, and what it connects to.",
    },
    {
        num: "03",
        title: "Build & Connect",
        text: "We implement the pieces — presence, website, WhatsApp follow-up, reviews — as one connected system, not scattered tactics.",
    },
    {
        num: "04",
        title: "Review & Improve",
        text: "We track what's actually working and keep tightening the system, month after month.",
    },
];

export const INDUSTRIES = [
    { icon: "UtensilsCrossed", name: "Restaurants" },
    { icon: "Coffee", name: "Cafes" },
    { icon: "Scissors", name: "Salons & Beauty" },
    { icon: "Stethoscope", name: "Clinics & Dental" },
    { icon: "Dumbbell", name: "Gyms & Fitness" },
    { icon: "GraduationCap", name: "Tutors & Coaching" },
    { icon: "Compass", name: "Coaches & Consultants" },
    { icon: "Briefcase", name: "Professional Services" },
    { icon: "Wrench", name: "Home Services" },
    { icon: "Building2", name: "Real Estate" },
    { icon: "BedDouble", name: "Hospitality" },
    { icon: "Store", name: "Local Services of Every Kind" },
];

export const AI_USES = [
    {
        title: "Faster first drafts",
        text: "Posts, offers and updates drafted in minutes, then reviewed by a human before anything goes out.",
    },
    {
        title: "Quicker replies",
        text: "AI-assisted responses and follow-up messages, so enquiries get an answer while they're still warm.",
    },
    {
        title: "Review workflows",
        text: "Timely review requests and thoughtful response drafts that keep your reputation active.",
    },
    {
        title: "Content repurposing",
        text: "One video or idea becomes weeks of short-form content instead of a single post.",
    },
    {
        title: "Sharper research",
        text: "Business and competitor research in minutes, so decisions are based on what's actually happening around you.",
    },
    {
        title: "Smoother operations",
        text: "Internal workflows that save your team hours every week on repetitive marketing tasks.",
    },
];

export const FAQS = [
    /* ---- General ---- */
    {
        q: "What does 033 Digital do?",
        a: "We build a simple three-stage growth funnel for local businesses. Get Found: your Google Business Profile and website, so nearby customers discover you. Get Booked: WhatsApp automation that turns enquiries into appointments and brings customers back. Get Growing: content, social media and ads to reach new people (coming soon). Each stage feeds the next.",
    },
    {
        q: "Who is it for?",
        a: "Local businesses that depend on customers discovering them online and contacting or booking them — clinics, salons, restaurants, cafes, gyms, tutors, coaches, home services, real estate, hospitality and other service-based local businesses.",
    },
    {
        q: "Do I need all three stages?",
        a: "No. You can start with just one. Many businesses begin with Get Found, so customers can find them on Google, then add WhatsApp automation once enquiries start coming in. The business scan tells you which stage is leaking the most, so you fix that first.",
    },

    /* ---- Stage 1: Get Found ---- */
    {
        q: "What do you do for our Google Business Profile?",
        a: "We set up or take over your profile and complete it properly: the right categories, services, hours, photos, description and regular posts. We keep it active and respond to reviews, so you show up when nearby customers search on Google and Maps.",
    },
    {
        q: "Do you build websites?",
        a: "Yes. We build fast, mobile-first websites made to turn visitors into enquiries, with clear call and WhatsApp buttons on every page. We can also improve an existing website instead of starting from scratch.",
    },
    {
        q: "Why do I need both a Google Business Profile and a website?",
        a: "Most local customers find you on Google first, then check your website before they call. Your profile gets you seen; your website builds trust and makes it easy to get in touch. Together they turn searches into enquiries.",
    },

    /* ---- Stage 2: Get Booked ---- */
    {
        q: "What can WhatsApp automation do for my business?",
        a: "It handles the repetitive messages for you: booking and confirming appointments, sending follow-ups to reduce no-shows, asking for a Google review after every visit, bringing old customers back, sending festive and promotional messages, and running bulk offer campaigns. It all runs automatically on WhatsApp, where your customers already are.",
    },
    {
        q: "How do appointment follow-ups reduce no-shows?",
        a: "Customers get an automatic confirmation when they book and a reminder before their appointment, with an easy way to reply if they need to reschedule. Fewer people forget, and you find out about changes early enough to fill the slot.",
    },
    {
        q: "How do review requests after every visit work?",
        a: "After each visit, the customer gets a friendly WhatsApp message with a direct link to leave you a Google review. Asking at the right moment is what gets happy customers to actually post. We never buy, fake or incentivise reviews — Google penalises it and customers can tell.",
    },
    {
        q: "Can you bring back old customers?",
        a: "Yes. We find past customers who haven't visited in a while and send them a warm, personal message — a reminder, a check-in or a return offer. Only customers who have opted in are messaged, and every message has an easy way to stop.",
    },
    {
        q: "Will bulk WhatsApp campaigns get my number blocked?",
        a: "Not when they're done the right way. Numbers get blocked when businesses blast messages from a regular WhatsApp number to people who never asked for them. We send campaigns through WhatsApp's official business platform, only to customers who have opted in, using approved message templates with a one-tap option to stop. That keeps your number safe and your messages welcome.",
    },
    {
        q: "Can you send festive and promotional messages?",
        a: "Yes. Diwali, Durga Puja, Eid, Christmas, New Year or your own anniversary sale — we schedule festive greetings and offers in advance, so they go out on time without you having to remember.",
    },

    /* ---- Stage 3: Get Growing ---- */
    {
        q: "Do you do social media and ads?",
        a: "That's our Get Growing stage — content creation, social media management, and Meta, Instagram and Google ads. It's coming soon. Message us on WhatsApp if you're interested and we'll let you know as soon as it launches.",
    },

    /* ---- Working with us ---- */
    {
        q: "What does 'AI-powered growth' actually mean?",
        a: "Practical things: faster content drafts, quicker replies and follow-ups, review requests and research that used to take hours. AI is a tool inside the system — not the product, and never a substitute for judgment.",
    },
    {
        q: "How does the business scan work?",
        a: "You share a few details about your business through the form or WhatsApp. We look at how customers currently find you, book you and come back, then walk you through where the leaks are and what we'd fix first.",
    },
    {
        q: "How do I get started?",
        a: "The simplest way: tap any WhatsApp button on this site and tell us about your business, or request a business scan. You'll hear back with real next steps — not a generic sales script.",
    },
    {
        q: "Do you guarantee Google rankings?",
        a: "No — and you should be cautious of anyone who does. Rankings depend on competition, your market and platforms we don't control. What we do control is building a strong, complete presence and improving it systematically.",
    },
    {
        q: "Do you guarantee leads or revenue?",
        a: "No. We don't promise specific numbers of leads, bookings or revenue. We build the system, implement it properly, measure honestly and keep optimising. That discipline — not guarantees — is what produces results.",
    },
];

export const BUSINESS_TYPES = [
    "Restaurant / Cafe",
    "Salon / Beauty",
    "Clinic / Dental",
    "Gym / Fitness Studio",
    "Coach / Tutor",
    "Professional Services",
    "Home Services",
    "Real Estate",
    "Hospitality",
    "Other Local Business",
];

export const CHALLENGES = [
    "Customers can't find me online",
    "My website doesn't convert visitors",
    "Enquiries and calls slip away",
    "Too few reviews",
    "Customers never come back",
    "Honestly — all of it",
];

/* ---------- Future-ready content structures ---------- */
/* When genuine, verifiable stories exist, add records here and the
   Results section renders them automatically. Never add invented data.
   Google reviews: name, business, source: "Google review", rating (1-5),
   date ("YYYY-MM-DD", shown as month + year) and the exact review text.
   The rating summary is calculated from these entries. */
export const TESTIMONIALS = [
    {
        name: "Deep Ganguly",
        business: "Security company, Kolkata",
        service: "Website design, SEO & Google Business Profile",
        source: "Google review",
        rating: 5,
        date: "2026-10-08",
        text: "As a business owner, finding 033 Digital was a game-changer. They handled the website design and SEO services for my security company perfectly. Not only does my new website look highly professional, but they also optimized my Google Business Profile so I can easily attract more local customers right here in Kolkata. I highly recommend this digital marketing agency to anyone looking to grow their business.",
    },
    {
        name: "Kasturi",
        business: "WhatsApp automation client",
        service: "WhatsApp automation",
        source: "Google review",
        rating: 5,
        date: "2026-10-09",
        text: "We onboarded 033 Digital to help us with WhatsApp automation. Already our customer queries have become streamlined and also we are getting back old customers through their targeted messaging. Kudos to the team.",
    },
    // {
    //   name: "", business: "", photo: "", logo: "", text: "",
    //   service: "", result: "", beforeAfter: null, video: "",
    //   sourceUrl: "", date: "", featured: false,
    // },
];

export const CASE_STUDIES = [
    // {
    //   client: "", industry: "", problem: "", changed: "", services: [],
    //   process: "", before: "", after: "", outcomes: [], screenshots: [],
    //   testimonial: "", slug: "",
    // },
];
